import { isNull } from '@core/tools';
import { channelStatusStyle, EChannelStatus, EOfflineReceiptStatus, EPublicCollectionStatus, offlineReceiptLabel, offlineReceiptSyle, publicCollectionLabel, publicCollectionSyle } from '../../types/common';
import { EYibaoAccountStatus, IYibaoBaseInfo, yibaoAccountStatusLabel, yibaoAccountStatusStyle } from '../../types/yibao';
import { computed, ComputedRef, Ref } from 'vue';

type TagColor = 'success' | 'warning' | 'danger' | 'info' | 'primary';

interface IYibaoWalletStatus {
  /** 公域节点显示？ */
  showElectronicStatus?: ComputedRef<boolean>;
  /** 线下节点显示？ */
  showOfflineStatus?: ComputedRef<boolean>;
  /** 通道相关 */
  channel?: ComputedRef<{
    status: EChannelStatus;
    label: string;
    color: TagColor;
  }>;
  /** 易宝账户相关 */
  status?: ComputedRef<{
    status?: EYibaoAccountStatus;
    label?: string;
    icon?: string;
    iconColor?: string;
    description?: string;
    color?: TagColor;
    errorReason?: string;
  }>;
  /** 公域电子户相关 */
  electronicStatus?: ComputedRef<{
    status?: EPublicCollectionStatus;
    label?: string;
    color?: TagColor;
    errorReason?: string;
  }>;
  /** 线下收款相关 */
  offlineStatus?: ComputedRef<{
    status?: EOfflineReceiptStatus;
    label?: string;
    color?: TagColor;
    errorReason?: string;
  }>;
}

const channelLabel = {
  [EChannelStatus.ENABLE]: '已启用',
  [EChannelStatus.DISABLE]: '已禁用',
};

export function useYibaoWalletStatus<T extends IYibaoBaseInfo>(info: Ref<T>, isAgent = false): IYibaoWalletStatus {
  const channel = computed(() => ({
    status: info.value?.channelStatus,
    label: channelLabel[info.value?.channelStatus] as string || '未配置',
    color: channelStatusStyle[info.value?.channelStatus] as any,
  }));

  const showElectronicStatus = computed(() => !!info.value.yeepayOnlinePublicCollectionIsOpen);
  const showOfflineStatus = computed(() => !!info.value.yeepayOfflineOfflineReceiptIsOpen);

  const status = computed(() => {
    let rs = {};

    const s = info.value?.merchantStatus;

    if (isNull(s) || s === EYibaoAccountStatus.DRAFT) {
      return {
        status: EYibaoAccountStatus.DRAFT,
        label: yibaoAccountStatusLabel[EYibaoAccountStatus.DRAFT] as string,
        color: yibaoAccountStatusStyle[EYibaoAccountStatus.DRAFT] as TagColor,
        description: ' 您还没有易宝账户，请申请',
        icon: 'material-symbols:error-outline-rounded',
        iconColor: '#BFBFBF',
      };
    }

    if (isAgent && s === EYibaoAccountStatus.AGREEMENT_SIGNING) {
      return {
        status: s,
        label: '待签署',
        color: yibaoAccountStatusStyle[s] as TagColor,
        description: '协议待签署',
        icon: 'material-symbols:error-outline-rounded',
        iconColor: '#BFBFBF',
      };
    }

    if (s === EYibaoAccountStatus.COMPLETED) {
      return {
        status: s,
        label: yibaoAccountStatusLabel[s] as string,
        color: yibaoAccountStatusStyle[s] as TagColor,
      };
    }

    switch (s) {
      case EYibaoAccountStatus.PENDING_SUBMIT:
        rs = {
          description: '资料待提交',
          icon: 'majesticons:clock-line',
          iconColor: '#3194FA',
        };
        break;
      case EYibaoAccountStatus.BUSINESS_OPENING:
      case EYibaoAccountStatus.REVIEWING:
        rs = {
          description: '易宝开户资料已提交，正在审核中...',
          icon: 'majesticons:clock-line',
          iconColor: '#3194FA',
        };
        break;
      case EYibaoAccountStatus.REVIEW_BACK:
        rs = {
          description: '易宝开户审核失败...',
          errorReason: info.value?.merchantStatusRemark,
          icon: 'material-symbols:cancel-outline-rounded',
          iconColor: '#FF4948',
        };
        break;
      case EYibaoAccountStatus.AGREEMENT_SIGNING:
        rs = {
          // description: '协议待签署',
          description: '账户开通中',
          icon: 'material-symbols:error-outline-rounded',
          iconColor: '#BFBFBF',
        };
        break;
      default:
        break;
    }

    return {
      status: info.value?.merchantStatus,
      label: yibaoAccountStatusLabel[s] as string,
      color: yibaoAccountStatusStyle[s] as TagColor,
      ...rs,
    };
  });

  const electronicStatus = computed(() => {
    let rs = {};

    const s = info.value?.publicCollectionStatus;
    const failed = [EPublicCollectionStatus.PRODUCT_MODIFY_REVIEW_BACK, EPublicCollectionStatus.BANK_OPENING_FAIL].includes(s);

    if (isNull(s) || s === EPublicCollectionStatus.DRAFT) {
      return {
        status: EPublicCollectionStatus.DRAFT,
        label: publicCollectionLabel[EPublicCollectionStatus.DRAFT] as string,
        color: publicCollectionSyle[EPublicCollectionStatus.DRAFT] as TagColor,
      };
    }

    return {
      status: s,
      label: publicCollectionLabel[s] as string,
      color: publicCollectionSyle[s] as TagColor,
      errorReason: failed ? info.value?.publicCollectionStatusRemark : undefined,
      ...rs,
    };
  });

  const offlineStatus = computed(() => {
    let rs = {};

    const s = info.value?.offlineReceiptStatus;
    const failed = [EOfflineReceiptStatus.PRODUCT_MODIFY_REVIEW_BACK].includes(s);

    if (isNull(s) || s === EOfflineReceiptStatus.DRAFT) {
      return {
        status: EOfflineReceiptStatus.DRAFT,
        label: offlineReceiptLabel[EOfflineReceiptStatus.DRAFT] as string,
        color: offlineReceiptSyle[EOfflineReceiptStatus.DRAFT] as TagColor,
      };
    }

    return {
      status: s,
      label: offlineReceiptLabel[s] as string,
      color: offlineReceiptSyle[s] as TagColor,
      errorReason: failed ? info.value?.offlineReceiptStatusRemark : undefined,
      ...rs,
    };
  });

  return {
    channel,
    status,
    electronicStatus,
    offlineStatus,
    showElectronicStatus,
    showOfflineStatus,
  };
}
